import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import InfoIcon from '@mui/icons-material/Info'
import { Alert, AlertColor, AlertProps, AlertTitle, Box, Divider } from '@mui/material'
import React, { FC, ReactNode } from 'react'

// biome-ignore lint/style/useComponentExportOnlyModules: Since this enum is specific to this component, it's ok to leave here
export enum BannerType {
  Error = 'error',
  Warning = 'warning',
  Success = 'success',
  Info = 'info'
}

type BannerProps = Omit<AlertProps, 'action'> & {
  type: BannerType
  title: string
  body?: string
  action?: ReactNode
  'data-testid'?: string
}

function mapBannerType(type: BannerType): { color: AlertColor; bgColor: string; borderColor: string; icon: React.ReactNode } {
  switch (type) {
    case BannerType.Error:
      return {
        color: 'error',
        bgColor: 'var(--bg-surface-error-transparent)',
        borderColor: 'var(--border-error-transparent)',
        icon: <ErrorIcon sx={{ color: 'var(--shape-error)' }} />
      }
    case BannerType.Warning:
      return {
        color: 'warning',
        bgColor: 'var(--bg-surface-warning-transparent)',
        borderColor: 'var(--border-focus-transparent)',
        icon: <WarningIcon sx={{ color: 'var(--shape-action)' }} />
      }
    case BannerType.Success:
      return {
        color: 'success',
        bgColor: 'var(--bg-surface-confirm-transparent)',
        borderColor: 'var(--border-confirm-transparent)',
        icon: <CheckCircleIcon sx={{ color: 'var(--shape-confirm)' }} />
      }
    case BannerType.Info:
      // TODO: Waiting for Jared to confirm the transparent color for info banners.
      // In the meantime, I'm applying the same transparency for the other colors
      return {
        color: 'info',
        bgColor: 'rgb(from var(--bg-surface-info) r g b / 0.3)',
        borderColor: 'rgb(from var(--bg-surface-info) r g b / 0.18)',
        icon: <InfoIcon sx={{ color: 'var(--shape-info)' }} />
      }
  }
}

const sharedSx = (backgroundColor: string) => ({
  color: 'var(--text-primary)',
  backgroundColor,
  borderRadius: 'var(--border-radius-l)'
})

/**
 * 2 render paths for this component depending on whether the `body` prop is provided:
 *
 * Title only (Banner):
 * - Horizontal layout: icon | title | optional action | optional close button
 * - Pass `onClose` to show the dismiss (×) button.
 * - Pass `action` to show an inline action element on the right.
 *   - if you pass both `onClose` and `action`, only the `action` will be shown. This is how MUI handles it, we're only passing the props through.
 *
 * Title + body (Alert):
 * - Stacked layout: icon | bold title / body text / optional footer action with divider
 * - `onClose` is ignored in this variant.
 * - Pass `action` to show it in a footer below a divider.
 *
 * Here's an example of how to use the `action` prop:
 * @example
 * action={
 *   <Button color="inherit" variant="text" sx={{ padding: 'var(--spacing-none)' }}>
 *     action
 *   </Button>
 * }
 */
export const Banner: FC<BannerProps> = ({ type, title, body, action, 'data-testid': dataTestId = 'banner', sx = {}, ...rest }) => {
  const { color, bgColor, icon, borderColor } = mapBannerType(type)

  if (body) {
    // Alert variant: stacked layout with AlertTitle, body, and optional footer action
    return (
      <Alert
        color={color}
        icon={icon}
        variant="filled"
        data-testid={dataTestId}
        slotProps={{
          message: { sx: { flex: 1, padding: 'var(--spacing-none)' } },
          icon: { sx: { marginRight: 'var(--spacing-2xs)', padding: 'var(--spacing-none)' } }
        }}
        sx={{
          ...sharedSx(bgColor),
          padding: 'var(--spacing-2xs)',
          fontSize: 'var(--size-s)',
          fontWeight: 400,
          ...sx
        }}
        {...rest}
        onClose={undefined}
      >
        <AlertTitle
          sx={{
            fontWeight: 500,
            fontSize: 'var(--size-s)',
            lineHeight: 'var(--line-height-s)',
            letterSpacing: 'var(--letter-spacing-xs)'
          }}
        >
          {title}
        </AlertTitle>
        {body}
        {action && (
          <Box sx={{ marginTop: 'var(--spacing-2xs)' }}>
            <Divider sx={{ borderColor }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>{action}</Box>
          </Box>
        )}
      </Alert>
    )
  }

  // Banner variant: single-line horizontal layout
  return (
    <Alert
      color={color}
      icon={icon}
      variant="filled"
      action={action}
      data-testid={dataTestId}
      sx={{
        ...sharedSx(bgColor),
        fontSize: 'var(--size-xs)',
        fontWeight: 300,
        lineHeight: 'var(--lineheight-m)',
        ...sx
      }}
      {...rest}
    >
      {title}
    </Alert>
  )
}
